import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, FileText, Calendar, RefreshCw, Eye, Trash2, ArrowLeft, Download } from 'lucide-react';
import { getSurveys, deleteSurvey } from '../utils/surveyApi';
import { getAccessCodeById } from '../utils/accessCodeApi';
import { getAuthHeaders } from '../utils/api';
import { API_BASE_URL, PDF_DISPLAY_GENERATE } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

const getPdfViewUrl = (sessionId) => {
  const baseUrl = API_BASE_URL.replace('/api', '');
  return `${baseUrl}/api/public/pdf-display/download/${sessionId}.pdf`;
};

const AccessCodeSurveys = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userSites } = useAuth();
  const [accessCode, setAccessCode] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAccessCode, setLoadingAccessCode] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [apiResponse, setApiResponse] = useState(null);
  const [downloadingSession, setDownloadingSession] = useState(null);
  const [selectedSurveys, setSelectedSurveys] = useState(new Set());
  const isFetchingRef = useRef(false);

  // Ensure surveys is always an array
  const safeSurveys = Array.isArray(surveys) ? surveys : [];

  // Get site_id from user login data (memoized to prevent unnecessary re-renders)
  const currentSiteId = useMemo(() => {
    return userSites?.site_id || user?.site_id || null;
  }, [userSites?.site_id, user?.site_id]);

  // Filter surveys based on search term and status "Processed"
  const filteredSurveys = safeSurveys.filter(survey => {
    // First filter by status "Processed"
    if (survey.status !== 'processed') {
      return false;
    }
    
    // Then filter by search term if provided
    if (!searchTerm) {
      return true;
    }
    
    const searchLower = searchTerm.toLowerCase();
    return (
      (survey.fname && survey.fname.toLowerCase().includes(searchLower)) ||
      (survey.lname && survey.lname.toLowerCase().includes(searchLower)) ||
      (survey.email && survey.email.toLowerCase().includes(searchLower)) ||
      (survey.access_code && survey.access_code.toLowerCase().includes(searchLower))
    );
  });

  // Fetch access code by ID
  useEffect(() => {
    const fetchAccessCode = async () => {
      if (!id) return;
      
      setLoadingAccessCode(true);
      try {
        const response = await getAccessCodeById(id);
        console.log('Access Code API Response:', response);
        
        if (response.success) {
          let accessCodeData = response.data;
          
          // Handle different possible API response structures
          if (response.data && response.data.data) {
            accessCodeData = response.data.data;
          } else if (response.data && response.data.access_code) {
            accessCodeData = response.data.access_code;
          }
          
          setAccessCode(accessCodeData);
        } else {
          console.error('Failed to fetch access code:', response.error);
        }
      } catch (error) {
        console.error('Error fetching access code:', error);
      } finally {
        setLoadingAccessCode(false);
      }
    };

    fetchAccessCode();
  }, [id]);

  // Fetch surveys filtered by access_code - memoized to prevent double calls
  const fetchSurveys = useCallback(async () => {
    if (!accessCode) return;
    
    // Prevent concurrent API calls
    if (isFetchingRef.current) {
      console.log('⏸️ Survey fetch already in progress, skipping...');
      return;
    }
    
    isFetchingRef.current = true;
    setLoading(true);
    try {
      // Prepare params with site_id and access_code filter
      const params = {};
      if (currentSiteId) {
        params.site_id = currentSiteId;
      }
      // Filter by access_code
      params.access_code = accessCode.code; 
      
      console.log('🔍 Fetching surveys with params:', params);
      
      // Try to fetch from API first
      const response = await getSurveys(params);
      

      // Always store the API response for display
      setApiResponse(response);
      
      if (response.success) {
        // Check if response.data exists and handle different data structures
        let surveyData = response.data.data;
        
        // Handle different possible API response structures
        if (response.data && response.data.data) {
          surveyData = response.data.data;
        } else if (response.data && response.data.surveys) {
          surveyData = response.data.surveys;
        } else if (response.data && response.data.items) {
          surveyData = response.data.items;
        } else if (response.data && response.data.results) {
          surveyData = response.data.results;
        }
        
        console.log('Processed survey data:', surveyData);


        
        console.log('Access Code:', accessCode.code);
        if (Array.isArray(surveyData)) {
          // Filter surveys by access_code to ensure they match
          const filtered = surveyData.filter(survey => 
            survey.access_code === accessCode.code
          );
          console.log('Filtered surveys by access_code:', filtered);
          setSurveys(filtered);
        } else {
          console.warn('API returned data but it\'s not an array:', surveyData);
          setSurveys([]);
        }
      } else {
        console.warn('API call failed:', response.error);
        setSurveys([]);
      }
    } catch (error) {
      console.warn('API error:', error);
      setSurveys([]);
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [accessCode, currentSiteId]);

  // Fetch surveys filtered by access_code
  useEffect(() => {
    if (accessCode) {
      fetchSurveys();
    }
  }, [accessCode, fetchSurveys]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      'processed': { label: 'Processed', className: 'bg-green-100 text-green-800' },
      'ongoing': { label: 'Ongoing', className: 'bg-yellow-100 text-yellow-800' },
      'pending': { label: 'Pending', className: 'bg-gray-100 text-gray-800' },
      'cancelled': { label: 'Cancelled', className: 'bg-red-100 text-red-800' }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const handleDownloadReport = async (survey, sessionId) => {
    if (!sessionId) {
      alert('No session ID available for this survey');
      return;
    }

    setDownloadingSession(sessionId);
    try {
      console.log('🚀 Calling PDF display generate API with session:', sessionId);
      const response = await fetch(`${API_BASE_URL}${PDF_DISPLAY_GENERATE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          session: sessionId,
          type: 'report',
          site_id: currentSiteId || 52
        })
      });

      const data = await response.json();
      console.log('📡 PDF Display Generate API Response:', data);

      if (data.success || data.data) {
        // Use download_url directly from response data
        const downloadUrl = data.data?.download_url || data.download_url || data.url;
        
        if (!downloadUrl) {
          // Fallback to default PDF view URL if no download_url provided
          const fallbackUrl = getPdfViewUrl(sessionId);
          console.warn('⚠️ No download_url in response, using fallback URL:', fallbackUrl);
          
          const link = document.createElement('a');
          link.href = fallbackUrl;
          link.download = `survey-report-${sessionId}-${new Date().getTime()}.pdf`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log('📥 PDF download initiated via fallback URL');
          return;
        }

        // Use the download_url directly from the response
        console.log('📥 Using download_url from response:', downloadUrl);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `survey-report-${sessionId}-${new Date().getTime()}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        console.log('✅ PDF download initiated successfully');
      } else {
        console.error('❌ Failed to generate PDF display:', data.error);
        alert('Failed to generate PDF: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error calling PDF display generate API:', error);
      alert('Error generating PDF: ' + error.message);
    } finally {
      setDownloadingSession(null);
    }
  };

  const handleViewReport = (survey) => {
    if (!survey.session) {
      alert('No session ID available for this survey');
      return;
    }
    const pdfUrl = getPdfViewUrl(survey.session);
    window.open(pdfUrl, '_blank');
  };

  const handleDelete = async (surveyId) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) {
      return;
    }

    try {
      const response = await deleteSurvey(surveyId);
      if (response.success) {
        setSurveys(surveys.filter(s => s.id !== surveyId));
      } else {
        alert('Failed to delete survey: ' + (response.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting survey:', error);
      alert('Error deleting survey: ' + error.message);
    }
  };

  // Checkbox handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(filteredSurveys.map(survey => survey.id || survey.session));
      setSelectedSurveys(allIds);
    } else {
      setSelectedSurveys(new Set());
    }
  };

  const handleSelectSurvey = (surveyId) => {
    const newSelected = new Set(selectedSurveys);
    if (newSelected.has(surveyId)) {
      newSelected.delete(surveyId);
    } else {
      newSelected.add(surveyId);
    }
    setSelectedSurveys(newSelected);
  };

  const isAllSelected = filteredSurveys.length > 0 && filteredSurveys.every(survey => 
    selectedSurveys.has(survey.id || survey.session)
  );
  const isIndeterminate = selectedSurveys.size > 0 && selectedSurveys.size < filteredSurveys.length;

  const handleExportCSV = () => {
    // If no surveys are selected, ask user if they want to export all
    let surveysToExport;
    if (selectedSurveys.size === 0) {
      const confirmExportAll = window.confirm(
        `No surveys selected. Do you want to export all ${filteredSurveys.length} filtered surveys?`
      );
      if (!confirmExportAll) {
        return;
      }
      surveysToExport = filteredSurveys;
    } else {
      // Export only selected surveys
      surveysToExport = filteredSurveys.filter(survey => 
        selectedSurveys.has(survey.id || survey.session)
      );
    }

    if (surveysToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Define CSV headers
    const headers = [
      'FIRST NAME',
      'LAST NAME',
      'EMAIL',
      'ACCESS DESCRIPTION',
      'Organizational Sustainability Awareness',
      'Personal Sustainability Commitment',
      'Sustainability Acumen',
      '1. How aware are you of all the contributing factors that lead to climate change?',
      '2. How aware are you of the global ambitions to reduce the effects of climate change?',
      '3. How aware are you of your own country’s ambition to reduce the effects of climate change?',
      '4. How knowledgeable are you of the impacts of climate change on our planet?',
      '5. How much have you witnessed firsthand the effects of climate change in your region?',
      'TOTAL %',
      '1. How aware are you of the global social issues that nations are trying to address?',
      '2. Do you understand the global social issues that may affect your organization?',
      '3. How aware are you of the ethical challenges that global businesses face?',
      '4. How prepared do you believe the next generations are to deal with the sustainability challenges ahead?',
      '5. How aware are you of the UNs 17 Sustainability Development Goals?',
      'TOTAL %',
      '1. How much should businesses be involved in reducing the effects of climate change?',
      '2. How much should businesses be involved in addressing global social issues?',
      '3. To what degree do you believe that climate change will affect your organization in a negative way?',
      '4. In your opinion what majority of businesses globally are driven mainly by profit?',
      '5. How much do you believe that new technology is key to solving our complex sustainability problems?',
      'TOTAL %',
      '1. How aware are you of any specific sustainability led projects in your organization that address environmental concerns?',
      '2. How much training have you attended in your organization around sustainability or climate change?',
      '3. How confident are you that have enough information around climate change to make effective sustainability decisions in your organization?',
      '4. How much awareness do you have of your organization’s strategic objective in sustainability for the next 2-5 years?',
      '5. Does your organization communicate internally on its sustainability goals?',
      'TOTAL %',
      '1. Does your organization actively support the physical and mental wellbeing of its employees?',
      '2. How much access do you have to professional growth through training in your organization?',
      '3. To what degree is a good work/life balance promoted in your organization?',
      '4. To what level is your organization actively promoting and following through with diversity equity and inclusion (DE&I) at all levels?',
      '5. How aware are you of any internal managers or committees working in environmental or social sustainability?',
      'TOTAL %',
      '1. How much do you believe it is the role of the organization to support the wellbeing of customers and community?',
      '2. How often does your organization offer ways to participate in charitable giving or volunteering activities?',
      '3. How much do you see it as your role to make any environmental or social impact changes at work?',
      '4. How robust is your engagement with external stakeholders such as customers suppliers or local communities to promote good practices and partnerships?',
      '5. How much would you welcome an increase of sustainable initiatives in your organization?',
      'TOTAL %',
      '1. How regularly do you make daily positive choices to minimize your own environmental impact in terms of Energy?',
      '2. How regularly do you make daily positive choices to minimize your own environmental impact in terms of Water?',
      '3. How regularly do you make daily positive choices to minimize your own environmental impact in terms of Waste?',
      '4. How much do you recycle reuse and reduce?',
      '5. Overall how much of an effort are you putting into reducing your own carbon footprint?',
      'TOTAL %',
      '1. How much do human rights and labour practices influence your personal purchasing decisions?',
      '2. How much do you actively seek out learning opportunities to become more knowledgeable around sustainability. e.g. read articles attend events?',
      '3. How much have you increased your personal sustainability action over the last 2 years?',
      '4. How active in the environmental community are you outside of the workplace?',
      '5. How often do you volunteer your time or participate in charitable work or giving outside of work?',
      'TOTAL %',
      '1. How much do your environmental habits at home match what you do in your workplace? e.g. reduce waste recycle reuse.',
      '2. If offered the chance to be part of a work-related internal impact team how likely would you be to accept?',
      '3. How much would an organizations strong sustainability reputation influence you choosing to work there?',
      '4. How much is climate change a concern for you personally?',
      '5. How much do you believe that all your personal efforts to be more sustainable are making a difference?',
      'TOTAL %',
      'DATE ADDED'
    ];

    // Convert data to CSV rows
    const csvRows = [
      headers.join(','),
      ...surveysToExport.map(survey => {
        return [
          `"${(survey.fname || '').replace(/"/g, '""')}"`,
          `"${(survey.lname || '').replace(/"/g, '""')}"`,
          `"${(survey.email || '').replace(/"/g, '""')}"`,
          `"${(survey.access_code || '').replace(/"/g, '""')}"`,
          survey.off_track || '0',
          survey.on_track || '0',
          survey.total_score || '0',
          survey.position1 || survey.Position1 || '0',
          survey.position2 || survey.Position2 || '0',
          survey.position3 || survey.Position3 || '0',
          survey.position4 || survey.Position4 || '0',
          survey.position5 || survey.Position5 || '0',
          survey.positionPercent || survey.PositionPercent || survey.position_percent || '0',
          survey.capability1 || survey.Capability1 || '0',
          survey.capability2 || survey.Capability2 || '0',
          survey.capability3 || survey.Capability3 || '0',
          survey.capability4 || survey.Capability4 || '0',
          survey.capability5 || survey.Capability5 || '0',
          survey.capabilityPercent || survey.CapabilityPercent || survey.capability_percent || '0',
          survey.channel1 || survey.Channel1 || '0',
          survey.channel2 || survey.Channel2 || '0',
          survey.channel3 || survey.Channel3 || '0',
          survey.channel4 || survey.Channel4 || '0',
          survey.channel5 || survey.Channel5 || '0',
          survey.channelPercent || survey.ChannelPercent || survey.channel_percent || '0',
          survey.buyer1 || survey.Buyer1 || '0',
          survey.buyer2 || survey.Buyer2 || '0',
          survey.buyer3 || survey.Buyer3 || '0',
          survey.buyer4 || survey.Buyer4 || '0',
          survey.buyer5 || survey.Buyer5 || '0',
          survey.buyerPercent || survey.BuyerPercent || survey.buyer_percent || '0',
          survey.communication1 || survey.Communication1 || '0',
          survey.communication2 || survey.Communication2 || '0',
          survey.communication3 || survey.Communication3 || '0',
          survey.communication4 || survey.Communication4 || '0',
          survey.communication5 || survey.Communication5 || '0',
          survey.communicationPercent || survey.CommunicationPercent || survey.communication_percent || '0',
          survey.conversations1 || survey.Conversations1 || '0',
          survey.conversations2 || survey.Conversations2 || '0',
          survey.conversations3 || survey.Conversations3 || '0',
          survey.conversations4 || survey.Conversations4 || '0',
          survey.conversations5 || survey.Conversations5 || '0',
          survey.conversationsPercent || survey.ConversationsPercent || survey.conversations_percent || '0',
          survey.product1 || survey.Product1 || '0',
          survey.product2 || survey.Product2 || '0',
          survey.product3 || survey.Product3 || '0',
          survey.product4 || survey.Product4 || '0',
          survey.product5 || survey.Product5 || '0',
          survey.productPercent || survey.ProductPercent || survey.product_percent || '0',
          survey.management1 || survey.Management1 || '0',
          survey.management2 || survey.Management2 || '0',
          survey.management3 || survey.Management3 || '0',
          survey.management4 || survey.Management4 || '0',
          survey.management5 || survey.Management5 || '0',
          survey.managementPercent || survey.ManagementPercent || survey.management_percent || '0',
          survey.service1 || survey.Service1 || '0',
          survey.service2 || survey.Service2 || '0',
          survey.service3 || survey.Service3 || '0',
          survey.service4 || survey.Service4 || '0',
          survey.service5 || survey.Service5 || '0',
          survey.servicePercent || survey.ServicePercent || survey.service_percent || '0',
          `"${survey.created_at ? new Date(survey.created_at).toLocaleString() : 'N/A'}"`
        ].join(',');
      })
    ];

    // Create CSV content
    const csvContent = csvRows.join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    const exportType = selectedSurveys.size > 0 ? `selected-${selectedSurveys.size}` : 'all';
    link.setAttribute('download', `surveys-${accessCode?.code || 'export'}-${exportType}-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const handleExportPDF = async () => {
    // Require explicit selection - no default export of all surveys
    if (selectedSurveys.size === 0) {
      alert('Please select at least 2 surveys to export as a group report PDF.');
      return;
    }
    
    // Export only selected surveys
    const surveysToExport = filteredSurveys.filter(survey => 
      selectedSurveys.has(survey.id || survey.session)
    );

    if (surveysToExport.length === 0) {
      alert('No data to export');
      return;
    }

    // Validate survey count: minimum 2, maximum 8
    if (surveysToExport.length < 2) {
      alert('Minimum 2 surveys are required to generate a group report PDF. Please select at least 2 surveys.');
      return;
    }

    if (surveysToExport.length > 8) {
      alert('Maximum 8 surveys are allowed for a group report PDF. Please select 8 or fewer surveys.');
      return;
    }

    // Extract survey IDs
    const surveyIds = surveysToExport
      .map(survey => survey.id)
      .filter(id => id !== undefined && id !== null);

    if (surveyIds.length === 0) {
      alert('No valid survey IDs found. Cannot generate group report.');
      return;
    }

    // Validate extracted survey IDs count
    if (surveyIds.length < 2) {
      alert('Minimum 2 valid survey IDs are required. Please ensure selected surveys have valid IDs.');
      return;
    }

    if (surveyIds.length > 8) {
      alert('Maximum 8 surveys are allowed. Please select 8 or fewer surveys.');
      return;
    }

    try {
      setDownloadingSession('group-report');
      console.log('🚀 Generating group report PDF for surveys:', surveyIds);
      
      //const baseUrl = API_BASE_URL.replace('/api', '');
      const response = await fetch(`${API_BASE_URL}/public/pdf-display/group-report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify({
          survey_ids: surveyIds,
          site_id: currentSiteId || 52,
          title: `Group Report - ${accessCode?.code || 'Surveys'}`
        })
      });

      const data = await response.json();
      console.log('📡 Group Report PDF API Response:', data);

      if (data.success || data.data) {
        const downloadUrl = data.data?.download_url || data.download_url || data.url;
        
        if (!downloadUrl) {
          throw new Error('No download URL received from API');
        }

        try {
          console.log('📥 Attempting to download group report PDF from:', downloadUrl);
          const pdfResponse = await fetch(downloadUrl, {
            method: 'GET',
            headers: { 'Accept': 'application/pdf' }
          });
          
          if (!pdfResponse.ok) {
            throw new Error(`Failed to fetch PDF: ${pdfResponse.status} ${pdfResponse.statusText}`);
          }
          
          const blob = await pdfResponse.blob();
          console.log('📦 PDF blob created, size:', blob.size);
          
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const exportType = selectedSurveys.size > 0 ? 'selected' : 'all';
          const reportTitle = accessCode?.code || 'group-report';
          link.download = `group-report-${reportTitle}-${exportType}-${surveyIds.length}-surveys-${new Date().toISOString().split('T')[0]}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          setTimeout(() => { window.URL.revokeObjectURL(url); }, 100);
          console.log('✅ Group report PDF downloaded successfully');
          alert(`Group report PDF downloaded successfully! (${surveyIds.length} survey${surveyIds.length > 1 ? 's' : ''})`);
        } catch (downloadError) {
          console.error('❌ Error downloading group report PDF:', downloadError);
          // Fallback to direct link
          const link = document.createElement('a');
          link.href = downloadUrl;
          const exportType = selectedSurveys.size > 0 ? 'selected' : 'all';
          const reportTitle = accessCode?.code || 'group-report';
          link.download = `group-report-${reportTitle}-${exportType}-${surveyIds.length}-surveys-${new Date().toISOString().split('T')[0]}.pdf`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          console.log('📥 Group report PDF download initiated via direct link (fallback)');
        }
      } else {
        console.error('❌ Failed to generate group report PDF:', data.error);
        alert('Failed to generate group report PDF: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('❌ Error calling group report PDF API:', error);
      alert('Error generating group report PDF: ' + error.message);
    } finally {
      setDownloadingSession(null);
    }
  };

  if (loadingAccessCode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" text="Loading access code..." />
      </div>
    );
  }

  if (!accessCode) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Code Not Found</h2>
          <p className="text-gray-600 mb-4">The access code you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/admin/access-codes')}
            className="btn-primary"
          >
            <ArrowLeft className="h-4 w-4 mr-2 inline" />
            Back to Access Codes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/access-codes')}
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Survey Data for Access Code: {accessCode.code}
            </h1>
            {/**<p className="text-sm text-gray-600 mt-1">
              {accessCode.value && `Company: ${accessCode.value}`}
            </p>*/}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="card">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or access code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportPDF}
              disabled={
                filteredSurveys.length === 0 || 
                downloadingSession !== null ||
                (selectedSurveys.size > 0 && (selectedSurveys.size < 2 || selectedSurveys.size > 8)) ||
                (selectedSurveys.size === 0 && (filteredSurveys.length < 2 || filteredSurveys.length > 8))
              }
              className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={
                selectedSurveys.size > 0 && (selectedSurveys.size < 2 || selectedSurveys.size > 8)
                  ? `PDF export requires 2-8 surveys. Currently selected: ${selectedSurveys.size}`
                  : selectedSurveys.size === 0 && (filteredSurveys.length < 2 || filteredSurveys.length > 8)
                  ? `PDF export requires 2-8 surveys. Available: ${filteredSurveys.length}`
                  : 'Export group report PDF (requires 2-8 surveys)'
              }
            >
              {downloadingSession ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              ) : (
                <FileText className="h-5 w-5" />
              )}
              <span>
                {selectedSurveys.size > 0 
                  ? `Export PDF (${selectedSurveys.size})` 
                  : `Export PDF`}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Surveys Table */}
      <div className="card">
        {/* Table Header Info */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900">Survey Responses</h3>
              {/**<p className="text-sm text-gray-600">
                {apiResponse && apiResponse.success 
                  ? `Showing ${filteredSurveys.length} of ${safeSurveys.length} surveys` 
                  : `Showing ${filteredSurveys.length} surveys`
                }
              </p>*/}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">
                Last updated: {new Date().toLocaleTimeString()}
              </p>
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="p-12 text-center">
            <LoadingSpinner size="lg" text="Loading surveys..." />
          </div>
        ) : (
          <div className="overflow-x-auto table-container">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={handleSelectAll}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </th>
                  {/**<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>*/}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    AccessCode
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Off Track
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    On Track
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position3
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position4
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position5
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    PositionPercent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capability1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capability2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capability3
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capability4
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Capability5
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CapabilityPercent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Channel1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Channel2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Channel3
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Channel4
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Channel5
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ChannelPercent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyer1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyer2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyer3
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyer4
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Buyer5
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    BuyerPercent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Communication1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Communication2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Communication3
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Communication4
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Communication5
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CommunicationPercent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversations1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversations2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversations3
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversations4
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversations5
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ConversationsPercent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product3
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product4
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product5
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ProductPercent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Management1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Management2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Management3
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Management4
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Management5
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ManagementPercent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service1
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service2
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service3
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service4
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Service5
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ServicePercent
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Created
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSurveys.length === 0 ? (
                  <tr>
                    <td colSpan="63" className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center space-y-2">
                        <FileText className="h-12 w-12 text-gray-300" />
                        <p className="text-lg font-medium">No surveys found</p>
                        <p className="text-sm">
                          {searchTerm ? 'No surveys match your search criteria' : 'No surveys found for this access code'}
                        </p>
                        <button
                          onClick={fetchSurveys}
                          className="btn-secondary mt-2"
                          style={{ backgroundColor: '#d64a68', color: '#ffffff' }}
                        >
                          Refresh
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredSurveys.map((survey) => {
                    const surveyId = survey.id || survey.session;
                    const isSelected = selectedSurveys.has(surveyId);
                    return (
                      <tr key={survey.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleSelectSurvey(surveyId)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                        </td>
                        {/**<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                if (downloadingSession) return;
                                handleDownloadReport(survey, survey.session);
                              }}
                              className={`text-primary-600 hover:text-primary-900 flex items-center justify-center ${downloadingSession === (survey.session || survey.id || 'survey') ? 'opacity-70 cursor-not-allowed' : ''}`}
                              style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px', minWidth: '70px' }}
                              disabled={downloadingSession === (survey.session || survey.id || 'survey')}
                            >
                              {downloadingSession === (survey.session || survey.id || 'survey') ? (
                                <span className="flex items-center space-x-1">
                                  <span className="inline-block h-3 w-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                  <span className="text-xs">Loading</span>
                                </span>
                              ) : (
                                <RefreshCw className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleViewReport(survey)}
                              className="text-blue-600 hover:text-blue-900"
                              style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(survey.id)}
                              className="text-red-600 hover:text-red-900"
                              style={{ backgroundColor: '#d64a68', color: '#ffffff', padding: '4px 8px', borderRadius: '4px' }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>*/}
                        <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{survey.fname || 'No First Name'}</div>
                          <div className="text-sm text-gray-500">{survey.lname || 'No Last Name'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-500">{survey.email || 'No Email'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm text-gray-500">{survey.access_code || 'No Access Code'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="text-red-600 font-medium">
                            {survey.off_track || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="text-green-600 font-medium">
                            {survey.on_track || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="text-blue-600 font-medium">
                            {survey.total_score || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.position1 || survey.Position1 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.position2 || survey.Position2 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.position3 || survey.Position3 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.position4 || survey.Position4 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.position5 || survey.Position5 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.positionPercent || survey.PositionPercent || survey.position_percent || '0'}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.capability1 || survey.Capability1 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.capability2 || survey.Capability2 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.capability3 || survey.Capability3 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.capability4 || survey.Capability4 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.capability5 || survey.Capability5 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.capabilityPercent || survey.CapabilityPercent || survey.capability_percent || '0'}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.channel1 || survey.Channel1 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.channel2 || survey.Channel2 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.channel3 || survey.Channel3 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.channel4 || survey.Channel4 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.channel5 || survey.Channel5 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.channelPercent || survey.ChannelPercent || survey.channel_percent || '0'}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.buyer1 || survey.Buyer1 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.buyer2 || survey.Buyer2 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.buyer3 || survey.Buyer3 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.buyer4 || survey.Buyer4 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.buyer5 || survey.Buyer5 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.buyerPercent || survey.BuyerPercent || survey.buyer_percent || '0'}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.communication1 || survey.Communication1 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.communication2 || survey.Communication2 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.communication3 || survey.Communication3 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.communication4 || survey.Communication4 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.communication5 || survey.Communication5 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.communicationPercent || survey.CommunicationPercent || survey.communication_percent || '0'}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.conversations1 || survey.Conversations1 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.conversations2 || survey.Conversations2 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.conversations3 || survey.Conversations3 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.conversations4 || survey.Conversations4 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.conversations5 || survey.Conversations5 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.conversationsPercent || survey.ConversationsPercent || survey.conversations_percent || '0'}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.product1 || survey.Product1 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.product2 || survey.Product2 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.product3 || survey.Product3 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.product4 || survey.Product4 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.product5 || survey.Product5 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.productPercent || survey.ProductPercent || survey.product_percent || '0'}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.management1 || survey.Management1 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.management2 || survey.Management2 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.management3 || survey.Management3 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.management4 || survey.Management4 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.management5 || survey.Management5 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.managementPercent || survey.ManagementPercent || survey.management_percent || '0'}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.service1 || survey.Service1 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.service2 || survey.Service2 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.service3 || survey.Service3 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.service4 || survey.Service4 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.service5 || survey.Service5 || '0'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center">
                          <span className="font-medium">
                            {survey.servicePercent || survey.ServicePercent || survey.service_percent || '0'}%
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span>
                            {survey.created_at ? new Date(survey.created_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </td>
                    </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessCodeSurveys;


